import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import Button from 'react-bootstrap/Button';
import {
  FiUpload, FiRefreshCw, FiGrid, FiList, FiTrash2, FiCopy,
  FiEye, FiFile, FiAlertTriangle, FiCheckCircle, FiChevronLeft, FiChevronRight,
} from 'react-icons/fi';

import toast, { Toaster } from 'react-hot-toast';

const StAssetLibraries = () => {
  const [acctSid, setAcctSid] = useState('');
  const [siteId, setSiteid] = useState('');
  const [act_e, setAct_E] = useState('');
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(9);

  const [totalPages] = useState([]);
  const [startAt, setStartAt] = useState('');
  const [imageUploaded, setImageUploaded] = useState(false);
  const [UploadUrl, setUploadUrl] = useState('');
  const [uploadsAllowed, setUploadsAllowed] = useState(true);
  const [ImageDeleted, setImageDeleted] = useState(false);
  const [reset,setReset] = useState(1)
  const [viewMode, setViewMode] = useState('grid');
  const [uploadLoading, setUploadLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const notify = () => toast.success('Copied to clipboard!');
  const errorNotify = (message) => toast.error(message);

  useEffect(() => {
    const acctId = localStorage?.getItem('turbosteedAcct') || '';
    const curSiteIdx = localStorage?.getItem('curSiteIdx') || '0';
    const currentSites = JSON.parse(localStorage?.getItem('sites') || '[]');
    const act_2_Turbo = localStorage?.getItem('act_e') || '';

    if (acctId) setAcctSid(acctId);
    if (act_2_Turbo) setAct_E(act_2_Turbo);
    if (currentSites && currentSites[curSiteIdx]) {
      setSiteid(currentSites[curSiteIdx]["siteId"]);
    }
  }, []);

  useEffect(() => {
    if (acctSid && siteId && act_e) {
      getAssets();
    }
  }, [acctSid, siteId, act_e]);

  const getAssets = async (fetchAssets) => {
    setLoading(true);
    setError('');
    try {
      totalPages.push(currentPage + 1);
      const fetchImageUrl = fetchAssets? `https://${process.env.REACT_APP_HOST_URL}/users/${acctSid}/sites/${siteId}/assets` : startAt !== ""? `https://${process.env.REACT_APP_HOST_URL}/users/${acctSid}/sites/${siteId}/assets?startAt=${startAt}` : `https://${process.env.REACT_APP_HOST_URL}/users/${acctSid}/sites/${siteId}/assets`;
      const response = await fetch(fetchImageUrl, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Authorization': 'Basic ' + btoa(acctSid + ':' + act_e),
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStartAt(data?.startAt);
        setUploadsAllowed(data?.uploadsAllowed);
        assets.push(...data.files || [])
        console.log("Fetched Data: ", assets);
      } else {
        throw new Error(`Failed to fetch assets: ${response.status}`);
      }
    } catch (error) {
      console.error("Error fetching assets", error);
      setError("Failed to load assets. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (filename) => {
    try {
      setDeleteLoading(true);
      const deleteUrl = `https://${process.env.REACT_APP_HOST_URL}/users/${acctSid}/sites/${siteId}/assets`;
      const response = await fetch(deleteUrl, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Authorization': 'Basic ' + btoa(acctSid + ':' + act_e),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ assetName: filename })
      });

      if (response.ok) {
        assets.length = 0;
        totalPages.length = 0;
        setCurrentPage(1);
        setImageDeleted(true);
        await getAssets(true);
      } else {
        throw new Error('Failed to delete asset');
      }
    } catch (error) {
      console.error("Error deleting asset", error);
      setError("Failed to delete asset. Please try again.");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) return;

    setUploadLoading(true);
    const formData = new FormData();
    formData.append('asset', uploadFile);

    try {
      const uploadUrl = `https://${process.env.REACT_APP_HOST_URL}/users/${acctSid}/sites/${siteId}/assets`;
      const response = await fetch(uploadUrl, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Authorization': 'Basic ' + btoa(acctSid + ':' + act_e),
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        assets.length = 0;
        totalPages.length = 0;
        setCurrentPage(1);
        setUploadUrl(data?.fileUrl);
        setImageUploaded(true);
        setUploadFile(null);
        await getAssets(true);
      } else {
        throw new Error('Failed to upload asset');
      }
    } catch (error) {
      console.error("Error uploading asset", error);
      if(error.status === 413){
        errorNotify("File size exceeds the 5mb. Please upload a smaller file.");
      }else if(error.status === 429){
        errorNotify("Assets Size limit exceeded. Please delete some assets to upload new ones.");
      }else{
        errorNotify("Upload failed. Please try again later or contact support");
      }
      setError("Failed to upload asset. Please try again.");
    } finally {
      setUploadLoading(false);
    }
  };

  const closeUploadModal = () => {
    setShowAddModal(false);
    setImageUploaded(false);
  };

  const closeDeleteModal = () => {
    setDeleteConfirm(null);
    setImageDeleted(false);
  };

  const getFileExtension = (filename) => {
    return filename?.split('.').pop()?.toLowerCase() || '';
  };

  const isImageFile = (filename) => {
    const extension = getFileExtension(filename);
    console.log("File Extension: ",filename + ' ' + extension)
    return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp', 'ico'].includes(extension);
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  async function NextPage() {
    try {
      console.log("Start At Value: ", startAt);
      if (startAt === "") return;
      setCurrentPage(currentPage + 1);
      await getAssets();
    } catch (error) {
      console.error("Error fetching next page", error);
    }

  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  console.log("Index of First Item: ", indexOfFirstItem);
  console.log("Index of Last Item: ", indexOfLastItem);
  const currentAssets = assets.slice(indexOfFirstItem, indexOfLastItem);
  // const totalPages = Math.ceil(assets.length / itemsPerPage);

  const getFileIcon = (filename) => {
    const extension = getFileExtension(filename);
    const iconMap = {
      pdf: 'fas fa-file-pdf text-danger',
      doc: 'fas fa-file-word text-primary',
      docx: 'fas fa-file-word text-primary',
      xls: 'fas fa-file-excel text-success',
      xlsx: 'fas fa-file-excel text-success',
      ppt: 'fas fa-file-powerpoint text-warning',
      pptx: 'fas fa-file-powerpoint text-warning',
      txt: 'fas fa-file-alt text-secondary',
      zip: 'fas fa-file-archive text-info',
      default: 'fas fa-file text-muted'
    };
    return iconMap[extension] || iconMap.default;
  };

  const btnBase = {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
    border: 'none', borderRadius: 'var(--radius-md)', fontSize: '13px',
    fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 150ms ease',
  };

  return (
    <div className="app-content animate-in">
      {/* Header */}
      <div className="dashboard-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 className="dashboard-title">Asset Library</h1>
          <p className="dashboard-subtitle">Manage your website files and media assets</p>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
          <button
            onClick={async () => { assets.length = 0; totalPages.length = 0; setCurrentPage(1); await getAssets(true); }}
            disabled={loading}
            style={{ ...btnBase, padding: '9px 14px', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
            title="Refresh"
          >
            <FiRefreshCw size={15} />
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            disabled={loading || !uploadsAllowed}
            style={{ ...btnBase, padding: '9px 18px', background: 'var(--accent-gradient)', color: 'white', opacity: (loading || !uploadsAllowed) ? 0.6 : 1 }}
          >
            <FiUpload size={14} /> Upload Asset
          </button>
        </div>
      </div>

      {/* Storage warning */}
      {!uploadsAllowed && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: '10px',
          background: 'var(--warning-soft)', border: '1px solid rgba(245,158,11,0.2)',
          borderRadius: 'var(--radius-md)', padding: '12px 16px', marginBottom: '24px',
          fontSize: '13px', color: 'var(--text-primary)',
        }}>
          <FiAlertTriangle size={16} style={{ color: 'var(--warning)', flexShrink: 0, marginTop: '1px' }} />
          <span>Upload is disabled — asset storage limit exceeded. Upgrade your plan or delete unused files to resume uploading.</span>
        </div>
      )}

      {/* View toggle */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
        <div style={{ display: 'flex', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
          {[['grid', FiGrid], ['list', FiList]].map(([mode, Icon]) => (
            <button
              key={mode}
              onClick={() => setViewMode(mode)}
              style={{
                padding: '7px 12px', border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                background: viewMode === mode ? 'var(--accent-soft)' : 'var(--bg-secondary)',
                color: viewMode === mode ? 'var(--accent-primary)' : 'var(--text-secondary)',
                borderLeft: mode === 'list' ? '1px solid var(--border-color)' : 'none',
              }}
            >
              <Icon size={15} />
            </button>
          ))}
        </div>
      </div>

      {/* Loading */}
      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '40vh' }}>
          <div style={{ textAlign: 'center' }}>
            <Spinner animation="border" style={{ width: '2rem', height: '2rem', color: 'var(--accent-primary)' }} />
            <div style={{ marginTop: '12px', fontSize: '13px', color: 'var(--text-tertiary)' }}>Loading assets...</div>
          </div>
        </div>
      ) : (
        <>
          {/* Grid view */}
          {viewMode === 'grid' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
              {currentAssets.map((asset, index) => (
                <div key={index} className="bento-card" style={{ padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                  {isImageFile(asset.filename) ? (
                    <div style={{
                      height: '160px', flexShrink: 0,
                      background: `url('${asset?.fileUrl}') center/cover no-repeat`,
                      borderBottom: '1px solid var(--border-color)',
                      backgroundColor: 'var(--bg-tertiary)',
                    }} />
                  ) : (
                    <div style={{
                      height: '160px', flexShrink: 0, display: 'flex', flexDirection: 'column',
                      alignItems: 'center', justifyContent: 'center',
                      background: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border-color)',
                    }}>
                      <FiFile size={32} style={{ color: 'var(--text-tertiary)', marginBottom: '8px' }} />
                      <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase' }}>
                        {getFileExtension(asset.filename)}
                      </span>
                    </div>
                  )}
                  <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <div style={{ fontSize: '13px', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: '4px' }} title={asset.filename}>
                      {asset.filename}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: 'var(--text-tertiary)', marginBottom: '10px' }}>
                      <span>{formatFileSize(asset.filesizeInBytes)}</span>
                      <span>{formatDate(asset.lastModified)}</span>
                    </div>
                    <div style={{ display: 'flex', gap: '6px', marginTop: 'auto' }}>
                      {isImageFile(asset.filename) && (
                        <a href={asset.fileUrl} target="_blank" rel="noopener noreferrer" title="View"
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)', color: 'var(--text-secondary)', textDecoration: 'none', flexShrink: 0 }}>
                          <FiEye size={13} />
                        </a>
                      )}
                      <button
                        onClick={() => { navigator.clipboard.writeText(asset.fileUrl); notify(); }}
                        style={{ flex: 1, ...btnBase, padding: '6px 8px', background: 'var(--bg-tertiary)', color: 'var(--text-secondary)', fontSize: '12px' }}
                      >
                        <FiCopy size={12} /> Copy URL
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(asset)}
                        disabled={loading}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', border: '1px solid var(--error-soft)', borderRadius: 'var(--radius-sm)', background: 'var(--error-soft)', color: 'var(--error)', cursor: 'pointer', flexShrink: 0 }}
                      >
                        <FiTrash2 size={13} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* List view */}
          {viewMode === 'list' && (
            <div className="bento-card" style={{ padding: 0, marginBottom: '24px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                    {['Name', 'Size', 'Date', 'Actions'].map((h, i) => (
                      <th key={h} style={{ padding: '11px 16px', textAlign: i === 3 ? 'right' : 'left', fontSize: '11px', fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {currentAssets.map((asset, index) => (
                    <tr key={index} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          {isImageFile(asset.filename) ? (
                            <div style={{ width: '36px', height: '36px', borderRadius: 'var(--radius-sm)', background: `url('${asset.fileUrl}') center/cover`, flexShrink: 0, backgroundColor: 'var(--bg-tertiary)' }} />
                          ) : (
                            <div style={{ width: '36px', height: '36px', borderRadius: 'var(--radius-sm)', background: 'var(--bg-tertiary)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              <FiFile size={16} style={{ color: 'var(--text-tertiary)' }} />
                            </div>
                          )}
                          <span style={{ fontSize: '13px', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '220px' }} title={asset.filename}>{asset.filename}</span>
                        </div>
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: '13px', color: 'var(--text-secondary)' }}>{formatFileSize(asset.filesizeInBytes)}</td>
                      <td style={{ padding: '12px 16px', fontSize: '13px', color: 'var(--text-secondary)' }}>{formatDate(asset.lastModified)}</td>
                      <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                          {isImageFile(asset.filename) && (
                            <a href={asset.fileUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '30px', height: '30px', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)', color: 'var(--text-secondary)', textDecoration: 'none' }}>
                              <FiEye size={13} />
                            </a>
                          )}
                          <button onClick={() => { navigator.clipboard.writeText(asset.fileUrl); notify(); }} style={{ ...btnBase, width: '30px', height: '30px', padding: 0, background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}>
                            <FiCopy size={13} />
                          </button>
                          <button onClick={() => setDeleteConfirm(asset)} disabled={loading} style={{ ...btnBase, width: '30px', height: '30px', padding: 0, background: 'var(--error-soft)', color: 'var(--error)' }}>
                            <FiTrash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
                style={{ ...btnBase, padding: '7px 12px', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', color: currentPage === 1 ? 'var(--text-tertiary)' : 'var(--text-primary)', opacity: currentPage === 1 ? 0.5 : 1 }}
              >
                <FiChevronLeft size={14} /> Prev
              </button>
              {totalPages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentPage(index + 1)}
                  style={{ ...btnBase, width: '36px', height: '36px', padding: 0, border: '1px solid var(--border-color)', background: index + 1 === currentPage ? 'var(--accent-primary)' : 'var(--bg-secondary)', color: index + 1 === currentPage ? 'white' : 'var(--text-primary)' }}
                >
                  {index + 1}
                </button>
              ))}
              <button
                disabled={startAt === '' ? currentPage === totalPages.length : false}
                onClick={async () => { setCurrentPage(currentPage + 1); if (startAt !== '') await getAssets(); }}
                style={{ ...btnBase, padding: '7px 12px', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', color: (startAt === '' && currentPage === totalPages.length) ? 'var(--text-tertiary)' : 'var(--text-primary)', opacity: (startAt === '' && currentPage === totalPages.length) ? 0.5 : 1 }}
              >
                Next <FiChevronRight size={14} />
              </button>
            </div>
          )}
        </>
      )}

      {/* Upload Modal */}
      <Modal
        show={showAddModal}
        onHide={imageUploaded ? closeUploadModal : () => { setImageUploaded(false); setShowAddModal(false); }}
        centered dialogClassName="modern-modal"
      >
        <Modal.Header
          closeButton
          onClick={imageUploaded ? closeUploadModal : () => { setImageUploaded(false); setShowAddModal(false); }}
        >
          <Modal.Title style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '28px', height: '28px', borderRadius: 'var(--radius-md)', background: 'var(--accent-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent-primary)' }}>
              <FiUpload size={14} />
            </div>
            {imageUploaded ? 'Upload Successful' : 'Upload New Asset'}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {imageUploaded ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 14px', background: 'var(--success-soft)', borderRadius: 'var(--radius-md)', fontSize: '13px', color: 'var(--success)' }}>
              <FiCheckCircle size={16} />
              File uploaded successfully! You can now copy the URL.
            </div>
          ) : (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Select File</Form.Label>
                <Form.Control type="file" onChange={(e) => setUploadFile(e.target.files[0])} style={{ padding: '10px', fontSize: '13px' }} />
                <Form.Text style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>
                  Supported: Images, PDFs, Documents, and more
                </Form.Text>
              </Form.Group>
              {uploadFile && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', background: 'var(--bg-tertiary)', borderRadius: 'var(--radius-md)', fontSize: '13px' }}>
                  <FiFile size={20} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />
                  <div>
                    <div style={{ fontWeight: 500 }}>{uploadFile.name}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>
                      {formatFileSize(uploadFile.size)} · {getFileExtension(uploadFile.name).toUpperCase()}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          {imageUploaded ? (
            <Button variant="primary" className="w-100" onClick={() => { navigator.clipboard.writeText(UploadUrl); notify(); closeUploadModal(); }}>
              Copy URL
            </Button>
          ) : (
            <>
              <Button variant="secondary" onClick={() => { setShowAddModal(false); setUploadFile(null); }} disabled={uploadLoading}>Cancel</Button>
              <Button variant="primary" disabled={uploadLoading} onClick={handleUpload}>
                {uploadLoading ? <><Spinner size="sm" className="me-2" />Uploading...</> : 'Upload Asset'}
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>

      {/* Delete Modal */}
      <Modal
        show={!!deleteConfirm}
        onHide={ImageDeleted ? closeDeleteModal : () => { setImageDeleted(false); setDeleteConfirm(null); }}
        centered dialogClassName="modern-modal"
      >
        <Modal.Header
          closeButton
          onClick={ImageDeleted ? closeDeleteModal : () => { setImageDeleted(false); setDeleteConfirm(null); }}
        >
          <Modal.Title style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '28px', height: '28px', borderRadius: 'var(--radius-md)', background: 'var(--error-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--error)' }}>
              <FiTrash2 size={14} />
            </div>
            {ImageDeleted ? 'File Deleted' : 'Confirm Delete'}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {ImageDeleted ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 14px', background: 'var(--success-soft)', borderRadius: 'var(--radius-md)', fontSize: '13px', color: 'var(--success)' }}>
              <FiCheckCircle size={16} />
              File deleted successfully.
            </div>
          ) : (
            <>
              <div style={{ background: 'var(--error-soft)', borderRadius: 'var(--radius-md)', padding: '12px 14px', fontSize: '13px', color: 'var(--text-primary)', marginBottom: '12px', lineHeight: 1.6 }}>
                <strong>This action cannot be undone.</strong> Are you sure you want to delete "<strong>{deleteConfirm?.filename}</strong>"?
              </div>
              {deleteConfirm && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px', background: 'var(--bg-tertiary)', borderRadius: 'var(--radius-md)', fontSize: '13px' }}>
                  <FiFile size={18} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />
                  <div>
                    <div style={{ fontWeight: 500 }}>{deleteConfirm.filename}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>{formatFileSize(deleteConfirm.filesizeInBytes)} · {formatDate(deleteConfirm.lastModified)}</div>
                  </div>
                </div>
              )}
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          {ImageDeleted ? (
            <Button className="w-100" onClick={closeDeleteModal}>OK</Button>
          ) : (
            <>
              <Button variant="secondary" onClick={() => setDeleteConfirm(null)} disabled={deleteLoading}>Cancel</Button>
              <Button variant="danger" onClick={() => handleDelete(deleteConfirm.filename)} disabled={deleteLoading}>
                {deleteLoading ? <><Spinner size="sm" className="me-2" />Deleting...</> : 'Delete Asset'}
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default StAssetLibraries;
